import { useLayoutEffect, useRef, useMemo, useCallback } from "react";
import { Flex, Box, chakra } from "@chakra-ui/react";

const EnhancedCircle = chakra("circle");
const EnhancedSvg = chakra("svg");

export default function CircularProgress({
  onClick,
  size = "56px",
  borderWidth = 2,
  color = "black",
  status = "running", // hidden|running|restingStart|restingCurrent|restingEnd
  progressTrail = true,
  progressTrailColor = "rgba(0, 0, 0, 0.2)",
  progressOnTop = false,
  getProgress,
  onFinish,
  children,
  ...rest
}) {
  const progressCircleRef = useRef();
  const animationFrameRequestIdRef = useRef();

  const radius = 20;
  const circleRadius = useMemo(() => radius - borderWidth / 2, [borderWidth]);
  const CIRCUMFERENCE = useMemo(
    () => 2 * Math.PI * circleRadius,
    [circleRadius]
  );
  const getStrokeDashoffset = useCallback(
    (progress) => CIRCUMFERENCE * (1 - Math.min(1, progress)),
    [CIRCUMFERENCE]
  );

  useLayoutEffect(() => {
    function runProgressUpdater() {
      const progress = getProgress();
      progressCircleRef.current.style.strokeDashoffset =
        getStrokeDashoffset(progress);
      if (progress >= 1) {
        onFinish();
        return;
      }
      animationFrameRequestIdRef.current =
        requestAnimationFrame(runProgressUpdater);
    }

    if (status === "restingStart") {
      progressCircleRef.current.style.strokeDashoffset = getStrokeDashoffset(0);
    }
    if (status === "restingEnd") {
      progressCircleRef.current.style.strokeDashoffset = getStrokeDashoffset(1);
    }
    if (status === "restingCurrent") {
      const progress = getProgress();
      progressCircleRef.current.style.strokeDashoffset =
        getStrokeDashoffset(progress);
    }
    if (status === "running") {
      runProgressUpdater();
    }
    return () => cancelAnimationFrame(animationFrameRequestIdRef.current);
  }, [status, getProgress, onFinish, getStrokeDashoffset]);

  return (
    <Box
      width={size}
      height={size}
      onClick={onClick}
      {...rest}
      position="relative"
    >
      {children && (
        <Flex
          justifyContent="center"
          alignItems="center"
          width="100%"
          height="100%"
          {...(progressOnTop && {
            position: "absolute",
            top: "0px",
            left: "0px"
          })}
        >
          {children}
        </Flex>
      )}
      {status !== "hidden" && (
        <EnhancedSvg
          width="100%"
          height="100%"
          viewBox="0 0 40 40"
          transform="rotate(-90deg)"
          {...(progressOnTop && {
            position: "absolute",
            top: "0px",
            left: "0px"
          })}
        >
          {progressTrail && (
            <EnhancedCircle
              fill="none"
              strokeLinecap="round"
              strokeDasharray={CIRCUMFERENCE}
              stroke={progressTrailColor}
              cx="20"
              cy="20"
              r={circleRadius}
              strokeWidth={borderWidth}
            />
          )}
          <EnhancedCircle
            ref={progressCircleRef}
            fill="none"
            strokeLinecap="round"
            strokeDasharray={CIRCUMFERENCE}
            stroke={color}
            cx="20"
            cy="20"
            r={circleRadius}
            strokeWidth={borderWidth}
          />
        </EnhancedSvg>
      )}
    </Box>
  );
}
